import React, { useCallback, useEffect, useState } from 'react';
import './PwaSheet.css';
import {
  isBadgeSupported,
  isStandaloneDisplay,
  needsBadgePermission,
  requestBadgePermission,
} from '../../Utils/appBadge';

const SNOOZE_KEY = 'famconnect:badge-permission-snoozed-until';
const SNOOZE_DAYS = 14;
const SHOW_DELAY_MS = 2000;

// No point asking a logged-out visitor to allow a count they cannot have yet.
function isLoggedIn() {
  try {
    return Boolean(window.localStorage.getItem('token'));
  } catch (error) {
    return false;
  }
}

function isSnoozed() {
  try {
    const until = Number(window.localStorage.getItem(SNOOZE_KEY));
    return Boolean(until) && Date.now() < until;
  } catch (error) {
    return false;
  }
}

function snooze() {
  try {
    window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));
  } catch (error) {
    // Private mode / storage disabled - the ask simply returns next launch.
  }
}

/**
 * Shown inside the installed app only. iOS will not paint a number on the home
 * screen icon until the web app has notification permission, and the request
 * has to come from a real tap - so this sheet exists to provide that tap.
 * Android grants badging without a permission and never sees this.
 */
function EnableBadgePrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isStandaloneDisplay() || !isBadgeSupported()) return undefined;
    if (!needsBadgePermission() || isSnoozed() || !isLoggedIn()) return undefined;

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleLater = useCallback(() => {
    snooze();
    setVisible(false);
  }, []);

  const handleEnable = useCallback(async () => {
    const permission = await requestBadgePermission();
    // A denial is the user's answer - don't ask again for a fortnight.
    if (permission !== 'granted') snooze();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="pwa-install-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwa-badge-title"
    >
      {/* data-no-drag: it is a bottom sheet, not a panel to move out of the way */}
      <div className="pwa-install-sheet" data-no-drag>
        <div className="pwa-install-head">
          <img
            className="pwa-install-icon"
            src={window.getPublicUrl ? window.getPublicUrl('/logo192.png') : '/logo192.png'}
            alt="FamConnect26"
          />
          <div>
            <h2 className="pwa-install-title" id="pwa-badge-title">
              Show your notification count
            </h2>
            <p className="pwa-install-subtitle">On the FamConnect26 app icon</p>
          </div>
        </div>

        <p className="pwa-install-body">
          Allow notifications and the number of unread notifications will appear on the
          FamConnect26 icon on your home screen, the way your other apps do.
        </p>

        <div className="pwa-install-actions">
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn--secondary"
            onClick={handleLater}
          >
            Not now
          </button>
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn--primary"
            onClick={handleEnable}
          >
            Allow
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnableBadgePrompt;
